import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { api } from '../../config';
import IdleTimeoutModal from './IdleTimeoutModal';
import {
  IDLE_TIMEOUT_MS,
  IDLE_WARNING_MS,
  HEARTBEAT_INTERVAL_MS,
  SESSION_ENDED_KEY,
  markActivity,
  getLastActivity,
  forceLogout,
} from '../../helpers/sessionManager';

// Idle 15 menit (modal di menit 14), heartbeat 30 dtk ke BE, expiry JWT, sinkron antar tab.
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'wheel',
  'scroll',
  'touchstart',
];

const getTokenExpiryMs = token => {
  try {
    const exp = JSON.parse(atob(token.split('.')[1])).exp;
    return exp ? exp * 1000 : null;
  } catch {
    return null;
  }
};

const sendHeartbeat = active =>
  axios
    .post(`${api.API_URL}/account/session/heartbeat`, { active })
    .catch(() => {}); // 401 berkode ditangani interceptor axios

const SessionMonitor = () => {
  const token = useSelector(state => state.Login?.user?.token);
  const [secondsLeft, setSecondsLeft] = useState(null); // null = modal tertutup
  const warningRef = useRef(false);
  const lastHeartbeatRef = useRef(Date.now());

  useEffect(() => {
    if (!token) return undefined;

    // 1. Buka ulang browser/tab setelah lama: lastActivity basi → langsung idle logout.
    const last = getLastActivity();
    if (last && Date.now() - last >= IDLE_TIMEOUT_MS) {
      forceLogout('IDLE_TIMEOUT', { notifyServer: true });
      return undefined;
    }
    if (!last) markActivity(true);

    // 2. Aktivitas user. Selama modal tampil, aktivitas pasif DIABAIKAN — hanya tombol yang dihitung.
    const onActivity = () => {
      if (!warningRef.current) markActivity();
    };
    ACTIVITY_EVENTS.forEach(e =>
      window.addEventListener(e, onActivity, { passive: true, capture: true })
    );

    // 3. Tab lain sudah logout / force logout → ikut keluar tanpa memanggil BE lagi.
    const onStorage = e => {
      if (e.key !== SESSION_ENDED_KEY || !e.newValue) return;
      try {
        forceLogout(JSON.parse(e.newValue).code, { broadcast: false });
      } catch {
        forceLogout('SESSION_INVALID', { broadcast: false });
      }
    };
    window.addEventListener('storage', onStorage);

    // 4. Tick 1 dtk: idle + expiry.
    const expiryMs = getTokenExpiryMs(token);
    const tick = setInterval(() => {
      const now = Date.now();
      if (expiryMs && now >= expiryMs) {
        forceLogout('SESSION_EXPIRED');
        return;
      }
      const idle = now - (getLastActivity() ?? now);
      if (idle >= IDLE_TIMEOUT_MS) {
        forceLogout('IDLE_TIMEOUT', { notifyServer: true });
      } else if (idle >= IDLE_TIMEOUT_MS - IDLE_WARNING_MS) {
        warningRef.current = true;
        setSecondsLeft(Math.ceil((IDLE_TIMEOUT_MS - idle) / 1000));
      } else if (warningRef.current) {
        // tab lain menekan "Stay signed in"
        warningRef.current = false;
        setSecondsLeft(null);
      }
    }, 1000);

    // 5. Heartbeat 30 dtk: deteksi sesi dicabut (single login) + laporkan aktivitas ke BE.
    // ponytail: tiap tab kirim heartbeat sendiri (N tab = N request/30 dtk); leader election bila jadi masalah.
    const heartbeat = setInterval(() => {
      const active = (getLastActivity() ?? 0) > lastHeartbeatRef.current;
      lastHeartbeatRef.current = Date.now();
      sendHeartbeat(active);
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach(e =>
        window.removeEventListener(e, onActivity, { capture: true })
      );
      window.removeEventListener('storage', onStorage);
      clearInterval(tick);
      clearInterval(heartbeat);
    };
  }, [token]);

  const staySignedIn = () => {
    warningRef.current = false;
    setSecondsLeft(null);
    markActivity(true);
    lastHeartbeatRef.current = Date.now();
    sendHeartbeat(true);
  };

  return (
    <IdleTimeoutModal
      isOpen={secondsLeft !== null}
      secondsLeft={secondsLeft ?? 0}
      onStay={staySignedIn}
      onLogout={() => forceLogout('USER_LOGOUT', { notifyServer: true })}
    />
  );
};

export default SessionMonitor;
